import { HEADER_EXPORT_TYPE, MIMETYPE_DISY_WORKORDER_ID, PARAMETER_SRS_CODE } from '@disy/lib.backend/api-constants';
import { MIMETYPE_JSON } from '@disy/lib/media-type';

import { OL_DEFAULT_MAP_SRS } from 'cadenza/acquisition/acquisition-layer-utils';
import type { AnalyticsExtensionParameterType } from 'cadenza/admin/analytics-extensions/model/analytics-extension-parameter-type.gen';
import type { SimpleGeometryType } from 'cadenza/admin/analytics-extensions/model/simple-geometry-type.gen';
import { ajax } from 'cadenza/api-client/api';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import type { Authority } from 'cadenza/api-client/srs-api';
import type { TableData, TableParams } from 'cadenza/api-client/tables-api';
import { tableParams } from 'cadenza/api-client/tables-api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { startWorkOrder } from 'cadenza/api-client/workorder-api';
import type { AttributeName } from 'cadenza/model/attribute-name.gen';
import type { DataAcquisitionActionType } from 'cadenza/repository/model/data-acquisition-action-type.gen';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { DataProtectionDescriptionDto } from 'cadenza/repository/model/data-protection-dto';
import type { DataSourceName } from 'cadenza/repository/model/data-source-name.gen';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ObjecttypeMemberInfoDto } from 'cadenza/repository/model/objecttype-member-info-dto';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { CapabilityKey } from 'cadenza/selfservice/import-data/api/capability-keys';
import type { ImportContentDescriptionDto } from 'cadenza/selfservice/import-data/api/core/import-content-description-dto';
import type { DataImportResultEvent } from 'cadenza/selfservice/import-data/api/data-import-result-event';
import type { ImportPresetDto } from 'cadenza/selfservice/import-data/api/import-preset-dto';
import type { PatchPresetRequestDto } from 'cadenza/selfservice/import-data/api/import-preset-request-dto';
import type { ImportPresetSummaryDto } from 'cadenza/selfservice/import-data/api/import-preset-summary-dto';
import type { ImportDataWizardTransition } from 'cadenza/selfservice/import-data/import-data-wizard-constants';
import type { MetadataMapping } from 'cadenza/selfservice/import-data/metadata-mapping';

export function checkIfObjecttypeDataCanBeExported(objecttypeId: string, mimeType: string) {
  return ajax<WorkOrderId>(`/objecttypes/${btoa(objecttypeId)}/data`, {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mimeType },
  });
}

export function exportObjecttypeData(objecttypeId: string, mimeType: string) {
  return ajax<WorkOrderId>(`/objecttypes/${btoa(objecttypeId)}/data`, {
    headers: { [HEADER_EXPORT_TYPE]: mimeType, Accept: MIMETYPE_DISY_WORKORDER_ID },
  });
}

export function createObjecttypeBasedOnTemplate(templateObjecttypeId: ObjecttypeIdString, dto: CreateObjecttypeDto) {
  const urlParams = new URLSearchParams({
    templateObjecttype: window.btoa(templateObjecttypeId),
  });
  return ajax<ObjecttypeInfoDto>(`/objecttypes?${urlParams}`, {
    type: 'POST',
    data: JSON.stringify(dto),
    contentType: MIMETYPE_JSON,
  });
}

export function createEmptyObjecttype(dto: CreateObjecttypeDto) {
  return ajax<ObjecttypeId>(`/objecttypes`, {
    type: 'POST',
    data: JSON.stringify(dto),
    contentType: MIMETYPE_JSON,
  });
}

export function createObjecttypeWithGeometry(
  geometryType: SimpleGeometryType,
  dto: CreateObjecttypeDto,
  srs: Authority,
  geometryAttributePrintName?: string,
  nameAttributePrintName?: string,
) {
  const urlParams = new URLSearchParams({ geometryType, [PARAMETER_SRS_CODE]: srs });
  if (geometryAttributePrintName) {
    urlParams.append('geometryAttributePrintName', geometryAttributePrintName);
  }
  if (nameAttributePrintName) {
    urlParams.append('nameAttributePrintName', nameAttributePrintName);
  }
  return ajax<ObjecttypeInfoDto>(`/objecttypes?${urlParams}`, {
    type: 'POST',
    data: JSON.stringify(dto),
    contentType: MIMETYPE_JSON,
  });
}

export function addDatasetForObjecttype(
  objecttypeId: ObjecttypeIdString,
  datasets: UpdateDataset[],
  dataAcquisitionActionType: DataAcquisitionActionType,
  srs?: string,
  returnGeneratedKeys: boolean = false,
) {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: srs ?? OL_DEFAULT_MAP_SRS,
    dataAcquisitionActionType,
    returnGeneratedKeys: returnGeneratedKeys ? 'true' : 'false',
  });
  return ajax<DatasetKeyValue[]>(`/objecttypes/${btoa(objecttypeId)}/data?${urlParams}`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(datasets),
  });
}

export function updateDatasetsForObjecttype(
  objecttypeId: ObjecttypeIdString,
  updateDataRecordDto: UpdateDataRecordDto,
  dataAcquisitionActionType: DataAcquisitionActionType,
  mapSrs?: string,
) {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS,
    dataAcquisitionActionType,
  });
  return ajax<void>(`/objecttypes/${btoa(objecttypeId)}/data?${urlParams}`, {
    type: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(updateDataRecordDto),
  });
}

export function deleteDataForObjecttype(
  objecttypeId: ObjecttypeIdString,
  keys: DatasetKey[],
  dataAcquisitionActionType: DataAcquisitionActionType,
) {
  const urlParams = new URLSearchParams({
    dataAcquisitionActionType,
  });
  return ajax<void>(`/objecttypes/${btoa(objecttypeId)}/data?${urlParams}`, {
    type: 'DELETE',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(keys),
  });
}

/**
 * A single dataset (row) of an objecttype, including all values from attribute relations right
 * after the affected attribute (e.g. geometry).
 * Property keys reflect the attribute's name while property values are the individual attribute
 * values.
 * Geometries are encoded as GeoJson string.
 */
export type DatasetValue = number | string | undefined;
export type Dataset = Record<string, DatasetValue>;
/**
 * Describes a change of an existing dataset.
 * Same as Dataset, but with different meaning for values:
 * - undefined -> the value will stay as is
 * - null -> the value will be cleared
 */
export type UpdateDataset = Record<string, DatasetValue | null>;
export type DatasetKeyValue = number | string;
export type DatasetKey = Record<string, DatasetKeyValue>;

/** See net/disy/cadenza/selfservice/acquisition/web/rest/dto/DatasetsWithMetadataDto.java */
export interface DatasetsWithMetadataDto {
  printNameAttributeName?: string;
  datasets: Dataset[];
}

/** @see net/disy/cadenza/selfservice/acquisition/web/rest/dto/UpdateDataRecordDto.java */
export interface UpdateDataRecordDto {
  keyValues: UpdateDataset[];
  values: UpdateDataset;
}

/** @see net/disy/cadenza/selfservice/acquisition/web/rest/dto/CreateObjecttypeDto.java */
export interface CreateObjecttypeDto {
  objecttypePrintName: string;
  repositoryName: RepositoryName;
  workbookName: WorkbookName;
  dataProtectionContextId?: DataProtectionContextId;
  dataProtectionDescription?: DataProtectionDescriptionDto;
}

export interface ObjecttypeInfoDto {
  objecttypeId: ObjecttypeId;
  objecttypePrintName: string;
  dataSourceName: DataSourceName;
  members: ObjecttypeMemberInfoDto[];
}

/**
 * Fetches the complete objecttype dataset for the given dataset id and objecttype id.
 * The objecttype must be a basic objecttype without hierarchies or function attributes
 * (pending further relaxation).
 * If no dataset keys are provided, all datasets are returned.
 *
 * @param objecttypeId - ID of the basic objecttype
 * @param keys - Names and values of the primary keys attributes, or undefined if all datasets should be queried.
 * @param srs - The SRS to use for the returned datasets. Defaults to the default map SRS "EPSG:3857".
 * @param includeGeometry - Whether to include the geometry attribute in the result. Defaults to true.
 * @return Returns the complete objecttype dataset for the given dataset id and objecttype id,
 *   including all values from attribute relations right after the affected attribute (e.g. geometry)
 */
export function loadDatasetsOfObjecttype(
  objecttypeId: ObjecttypeIdString,
  keys?: DatasetKey[],
  srs?: string,
  includeGeometry = true,
): Promise<DatasetsWithMetadataDto> {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: srs ?? OL_DEFAULT_MAP_SRS,
    includeGeometry: includeGeometry ? 'true' : 'false',
  });
  return ajax<DatasetsWithMetadataDto>(`/objecttypes/${btoa(objecttypeId)}/data?${urlParams}`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: keys ? JSON.stringify(keys) : undefined,
  });
}

/** See net/disy/cadenza/selfservice/acquisition/web/rest/dto/KeyListValueDto.java */
export interface KeyListValueDto {
  keyAttributeValue: DatasetValue;
  formattedDisplayAttributeValue: string;
}

export function getKeyListValues(
  objecttypeId: ObjecttypeIdString,
  attributeName: AttributeName,
  params: TableParams = {},
) {
  return ajax<TableData<KeyListValueDto>>(
    `/objecttypes/${btoa(objecttypeId)}/attributes/${btoa(attributeName)}/key-list-values?${tableParams(params)}`,
  );
}

export function getImportVariants() {
  return ajax('/selfservice/import/variants', {
    headers: {
      Accept: MIMETYPE_JSON,
    },
  });
}

export type MetadataState = { [key in CapabilityKey]?: MetadataMapping[key] };

export function getImportMetadata({
  mediaType,
  files,
  settings,
  metadataTarget,
}: {
  mediaType: string;
  files: UploadedFileDto[];
  settings: object;
  metadataTarget: CapabilityKey[];
}) {
  return ajax<Partial<MetadataState>>('/selfservice/import/metadata', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({
      // see SelfServiceImportMetadataRequestDto
      mediaType,
      files,
      settings,
      metadataTarget,
    }),
  });
}

interface SelfServiceImportPreviewRequestDto {
  mediaType: string;
  files: UploadedFileDto[];
  settings: object;
}

export function getPreviews({ mediaType, files, settings }: SelfServiceImportPreviewRequestDto) {
  return ajax('/selfservice/import/preview', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({
      mediaType,
      files,
      settings,
    }),
  });
}

export interface SelfServiceImportRequestDto {
  mediaType: string;
  files: UploadedFileDto[];
  settings: object; // can be empty, see comment on startHeadlessImport
  workbookId: WorkbookId;
}

export interface SelfServiceFastForwardState {
  settings: object;
  forwardedTransition?: ImportDataWizardTransition;
  isComplete: boolean;
}

export function startImport(options: {
  data: SelfServiceImportRequestDto;
  onProgress?: (event: WorkOrderProgressEvent) => void;
}): Promise<DataImportResultEvent> {
  return startWorkOrder('/selfservice/import-work-orders', 'self-service-data-import-result', options);
}

/**
 * Allows starting an import with minimal settings, in many cases they can even be empty. Settings
 * that are empty but required will be set to heuristically chosen or default settings. Settings
 * that are available will be used as-is, potentially causing followup errors, e.g. when missing attributes
 * are referenced or mandatory settings are missing.
 *
 * The mediaType, files and workbookId parameters are mandatory.
 *
 * If the given file(s) contain multiple contents that could be imported it will usually make sense to
 * define what contents should be imported.
 * By default, a new objecttype will be created with a default name that might clash with existing objecttype names,
 * to overwrite this behavior the appropriate settings need to be provided.
 * CAREFUL: if the target workbook has data protection contexts enabled the corresponding settings should be provided, even if
 * there is no backend-side validation! The UI will not allow headless import in this case.
 */
export function startHeadlessImport(options: {
  data: SelfServiceImportRequestDto;
  onProgress?: (event: WorkOrderProgressEvent) => void;
}): Promise<DataImportResultEvent> {
  return startWorkOrder('/selfservice/import-headless-work-orders', 'self-service-data-import-result', options);
}

/**
 * Takes the given (usually incomplete) import request data and attempts to fast-forward it,
 * starting at the given transition, so that all defaults, heuristics, and validations are applied
 * and the effective settings are returned.
 *
 * If validation fails or (user) input is required,
 * it will return again an incomplete state and indicate what transition to show.
 */
export function fastForwardImport(
  data: SelfServiceImportRequestDto,
  currentTransition?: ImportDataWizardTransition,
): Promise<SelfServiceFastForwardState> {
  return ajax(`/selfservice/import/fast-forward/${currentTransition || ''}`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(data),
  });
}

export interface AnalyticsExtensionParameterDefinition {
  name: string;
  printName: string;
  parameterType: AnalyticsExtensionParameterType;
  options?: string[];
  required?: boolean;
  defaultValue?: unknown;
  requestedSrs?: string;
  geometryTypes?: Extract<
    SimpleGeometryType,
    'POINT' | 'MULTIPOINT' | 'LINESTRING' | 'MULTILINESTRING' | 'POLYGON' | 'MULTIPOLYGON'
  >[];
}

export function savePreset(importPresetDto: ImportPresetDto) {
  return ajax('/selfservice/presets', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(importPresetDto),
  });
}

export function patchPreset(patchPresetRequestDto: PatchPresetRequestDto) {
  const fileExtension = patchPresetRequestDto.fileExtension;
  const importPresetId = patchPresetRequestDto.importPresetId;
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, {
    type: 'PATCH',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({
      printName: patchPresetRequestDto.printName,
      settings: patchPresetRequestDto.settings,
    }),
  });
}

export function listPresets(fileExtension: string): Promise<ImportPresetSummaryDto[]> {
  return ajax(`/selfservice/presets/${fileExtension}`, { type: 'GET' });
}

export function getPreset(fileExtension: string, importPresetId: string): Promise<ImportPresetDto> {
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, { type: 'GET' });
}

export function deletePreset({ fileExtension, importPresetId }: { fileExtension: string; importPresetId: string }) {
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, {
    type: 'DELETE',
  });
}

export function getSubContent(extractedUploadedFileDto: UploadedFileDto) {
  return ajax<ImportContentDescriptionDto[]>('/selfservice/import/archive-content', {
    data: {
      originalFileName: extractedUploadedFileDto.originalFileName,
      fileName: extractedUploadedFileDto.fileName,
      signature: extractedUploadedFileDto.signature,
    },
    type: 'GET',
  });
}
